import { useMemo } from "react";
import { Box, SelectChangeEvent, SxProps, Theme } from "@mui/material";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import WidgetMenuSelector from "@arelia-components/ui-atoms/WidgetMenuSelector";
import { PlatformPost, PostForMeAccount } from "../../api/postForMe";
import { DataGrid } from "@arelia-components/ui-atoms/data-grid/DataGrid";
import { EmptyMessage } from "@arelia-components/ui-atoms/EmptyMessage";
import { Inventory2Outlined } from "@mui/icons-material";
import { useSortedPosts, buildColumns, getPlatformConfig } from "./util";
import { ALL_ACCOUNTS_ID } from "./models";

interface PostsAnalyticsPresenterProps {
  accounts: PostForMeAccount[] | undefined;
  isLoadingAccounts: boolean;
  selectedAccountId: string;
  onAccountChange: (event: SelectChangeEvent<string>) => void;
  posts: PlatformPost[];
  isLoadingFeed: boolean;
  error?: Error | null;
}

export default function PostsAnalyticsPresenter({
  accounts,
  isLoadingAccounts,
  selectedAccountId,
  onAccountChange,
  posts,
  isLoadingFeed,
  error
}: PostsAnalyticsPresenterProps) {
  const { sortedPosts, sortConfig, handleSort } = useSortedPosts(posts);

  const columns = useMemo(
    () => buildColumns(sortConfig, handleSort),
    [sortConfig, handleSort]
  );

  const handleAccountSelect = (value: string) => {
    onAccountChange({ target: { value } } as SelectChangeEvent<string>);
  };

  const menuItems = useMemo(() => {
    if (!accounts) return [];

    return [
      { label: "All Accounts", value: ALL_ACCOUNTS_ID },
      ...accounts.map(account => ({
        label: (
          <Box component="span" sx={styles.menuItemLabel}>
            {getPlatformConfig(account.platform)?.menuIcon}
            {account.username}
          </Box>
        ),
        value: account.id
      }))
    ];
  }, [accounts]);

  const renderAccountSelector = () => (
    <Box sx={styles.headerSelector}>
      <WidgetMenuSelector<string>
        onClick={handleAccountSelect}
        selectedValue={selectedAccountId}
        menuItems={menuItems}
      />
    </Box>
  );

  return (
    <WidgetCard
      widgetId="POSTS_ANALYTICS"
      title="Posts Analytics"
      headerComponent={!isLoadingAccounts && accounts?.length ? renderAccountSelector() : undefined}
      loading={isLoadingAccounts || isLoadingFeed}
      error={!!error && !isLoadingAccounts && !isLoadingFeed}
    >
      <Box sx={styles.cardContent}>
        {!accounts?.length ? (
          <Box sx={styles.emptyContainer}>
            <EmptyMessage icon={Inventory2Outlined} message={"No connected social accounts found or your subscription does not include My Social feature"} />
          </Box>
        ) : (
          <DataGrid<PlatformPost>
            rows={sortedPosts}
            columns={columns}
            pageSize={10}
            rowsPerPage={[10, 20, 50]}
          />
        )}
      </Box>
    </WidgetCard>
  );
}

const styles = {
  headerSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  menuItemLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  cardContent: {
    flex: 1,
    overflow: 'hidden',
    height: '100%',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
};
